import React, { useMemo } from "react";
import { Text, Stack, Group } from "@mantine/core";

import {
  formatDateTime,
  formatDaysOfWeek,
  formatDuration,
  formatUTCDateRange,
  formatUTCTimeRange,
} from "@/utils";
import { ContentBlock } from "@/utils/api";

import {
  AppTable,
  SortConfig,
  AppTableColumn,
  RecordActions,
  StatusBadge,
  AppButton,
} from "@/components/generic";

import { ContentTypeBadge } from "../ContentTypeBadge";

export interface ContentBlocksTableProps {
  /** Array of content blocks to display */
  contentBlocks: ContentBlock[];

  /** Loading state for initial load */
  loading: boolean;

  /** Loading state for loading more items */
  loadingMore: boolean;

  /** Whether there are more items to load */
  hasMore: boolean;

  /** Current sort configuration */
  sortBy: SortConfig[];

  /** Callback when sort changes */
  onSortChange: (sortBy: SortConfig[]) => void;

  /** Callback to load more items */
  onLoadMore: () => void;

  /** Callback when edit action is clicked */
  onEditContentBlock?: (contentBlock: ContentBlock) => void;

  /** Callback when duplicate action is clicked */
  onDuplicateContentBlock?: (contentBlock: ContentBlock) => void;

  /** Callback when remove action is clicked */
  onRemoveContentBlock?: (contentBlock: ContentBlock) => void;

  /** Callback when view action is clicked */
  onViewContentBlock?: (contentBlock: ContentBlock) => void;

  /** Callback when assign to displays action is clicked */
  onAssignContentBlock?: (contentBlock: ContentBlock) => void;

  onAssignEmergencyContentBlock?: (contentBlock: ContentBlock) => void;

  /** ID of content block currently being removed */
  removingContentBlockId?: string | null;

  /** Table size */
  size?: "xs" | "sm" | "md" | "lg" | "xl";

  editedContentBlockId?: string | null | undefined;
}

export const ContentBlocksTable: React.FC<ContentBlocksTableProps> = ({
  contentBlocks,
  loading,
  loadingMore,
  hasMore,
  sortBy,
  onSortChange,
  onLoadMore,
  onEditContentBlock,
  onDuplicateContentBlock,
  onRemoveContentBlock,
  onViewContentBlock,
  onAssignContentBlock,
  onAssignEmergencyContentBlock,
  removingContentBlockId,
  editedContentBlockId,
  size = "md",
}) => {
  // Define table columns
  const columns: AppTableColumn<ContentBlock>[] = useMemo(
    () => [
      {
        key: "name",
        label: "Content Block",
        sortable: true,
        flex: true,
        minWidth: 180,
        render: (value, record) => (
          <Stack gap={"xs"}>
            <Text fw={600} lineClamp={1}>
              {value}
            </Text>
            {record.description && (
              <Text size="sm" c="dimmed" lineClamp={2}>
                {record.description}
              </Text>
            )}
            <ContentTypeBadge type={record.contentType} />
          </Stack>
        ),
      },
      {
        key: "schedule",
        label: "Schedule",
        sortable: false,
        width: 220,
        minWidth: 220,
        hideOnMobile: true,
        render: (_, record) => {
          const hasDateRange = record.startDatetime || record.endDatetime;
          const hasTimeRange = record.startTime || record.endTime;
          const hasRecurrence =
            record.recurrencePattern &&
            record.recurrencePattern.toUpperCase() !== "NONE";

          // If no schedule info at all
          if (!hasDateRange && !hasTimeRange && !hasRecurrence) {
            return (
              <Text size="sm" c="dimmed">
                Always active
              </Text>
            );
          }

          return (
            <Stack gap={2}>
              {/* Date Range */}
              {hasDateRange && (
                <Group gap={4} wrap="nowrap">
                  <Text size="xs" c="dimmed" style={{ minWidth: "35px" }}>
                    Dates:
                  </Text>
                  <Text size="sm" lineClamp={1}>
                    {formatUTCDateRange(
                      record.startDatetime,
                      record.endDatetime
                    )}
                  </Text>
                </Group>
              )}

              {/* Time Range */}
              {hasTimeRange && (
                <Group gap={4} wrap="nowrap">
                  <Text size="xs" c="dimmed" style={{ minWidth: "35px" }}>
                    Time:
                  </Text>
                  <Text size="sm" lineClamp={1}>
                    {formatUTCTimeRange(record.startTime, record.endTime)}
                  </Text>
                </Group>
              )}

              {/* Recurrence Pattern */}
              {hasRecurrence && (
                <Group gap={4} wrap="nowrap">
                  <Text size="xs" c="dimmed" style={{ minWidth: "35px" }}>
                    Days:
                  </Text>
                  <Text size="sm" lineClamp={1}>
                    {formatDaysOfWeek(
                      record.recurrenceDays,
                      record.recurrencePattern
                    )}
                  </Text>
                </Group>
              )}
            </Stack>
          );
        },
      },
      {
        key: "durationSeconds",
        label: "Duration",
        sortable: true,
        width: 120,
        minWidth: 120,
        align: "left",
        hideOnMobile: true,
        render: (value) => (
          <Text size="sm" c={value ? "currentColor" : "dimmed"}>
            {formatDuration(value)}
          </Text>
        ),
      },
      {
        key: "status",
        label: "Status",
        sortable: true,
        width: 120,
        minWidth: 120,
        align: "left",
        hideOnMobile: true,
        render: (value) => {
          return <StatusBadge status={value} />;
        },
      },
      {
        key: "updatedAt",
        label: "Updated",
        sortable: true,
        width: 120,
        minWidth: 120,
        render: (_, record) => (
          <Text size="sm" c="dimmed">
            {formatDateTime(record.updatedAt || record.createdAt, "datetime")}
          </Text>
        ),
      },
      {
        key: "actions",
        label: "",
        width: 300,
        minWidth: 300,
        align: "left",
        render: (_, record) => (
          <Group gap={0} justify="flex-end">
            <AppButton
              key="playlist"
              variant="tertiary"
              iconName="PlaylistAdd"
              size={"xs"}
              onClick={() =>
                typeof onAssignContentBlock == "function" &&
                onAssignContentBlock(record)
              }
              tooltip="Assign the content block to playlists"
            />
            <AppButton
              key="emergency"
              variant="tertiary"
              iconName="Urgent"
              size={"xs"}
              onClick={() =>
                typeof onAssignEmergencyContentBlock == "function" &&
                onAssignEmergencyContentBlock(record)
              }
              tooltip="Assign the content block as emergency message to displays"
            />
            <RecordActions
              context={record}
              //onViewClick={onAssignEmergencyContentBlock}
              onEditClick={onEditContentBlock}
              onDuplicateClick={onDuplicateContentBlock}
              onRemoveClick={onRemoveContentBlock}
              //onGoInClick={onAssignContentBlock}
              removeInProgress={removingContentBlockId === record.id}
            />
          </Group>
        ),
      },
    ],
    [
      onViewContentBlock,
      onEditContentBlock,
      onDuplicateContentBlock,
      onRemoveContentBlock,
      onAssignContentBlock,
      onAssignEmergencyContentBlock,
      removingContentBlockId,
    ]
  );

  return (
    <AppTable
      data={contentBlocks}
      hideOnNoData={true}
      columns={columns}
      loading={loading}
      hasMore={hasMore}
      loadingMore={loadingMore}
      onLoadMore={onLoadMore}
      sortBy={sortBy}
      onSortChange={onSortChange}
      multiSort={false}
      getRowKey={(contentBlock) => contentBlock?.id || ""}
      withBorder
      highlightOnHover
      highlightRow={(contentBlock) => contentBlock?.id == editedContentBlockId}
      size={size}
    />
  );
};
